import { env } from "@/env";
import { PHASE_PRODUCTION_BUILD } from "next/constants";
import { pino, type Logger } from "pino";

function isLongContentToRedact(value: string): boolean {
  if (value.length < 1000) {
    return false;
  }

  if (value.startsWith("data:image/")) {
    return true;
  }

  if (value.length > 2000) {
    return true;
  }

  return /^[A-Za-z0-9+/=\s]+$/.test(value);
}

function redactLongContent(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === "string") {
    if (obj.length > 999 && isLongContentToRedact(obj)) {
      return "[REDACTED_CONTENT]";
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(redactLongContent);
  }

  if (typeof obj === "object") {
    let hasChanges = false;
    const redacted: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      const redactedValue = redactLongContent(value);
      redacted[key] = redactedValue;
      if (redactedValue !== value) {
        hasChanges = true;
      }
    }

    return hasChanges ? redacted : obj;
  }

  return obj;
}

const isServer = typeof window === "undefined";
const isBuildTime =
  isServer && process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD;

function createConsoleLogger(bindings: Record<string, unknown> = {}): Logger {
  const log =
    (level: string, consoleMethod: (...args: unknown[]) => void) =>
    (...args: unknown[]) => {
      const [first, second] = args;
      const obj = typeof first === "object" && first !== null ? first : {};
      const msg =
        typeof second === "string"
          ? second
          : typeof first === "string"
            ? first
            : undefined;
      const payload = redactLongContent({
        ...bindings,
        ...obj,
        ...(msg && { msg }),
      }) as Record<string, unknown>;
      if (msg) {
        consoleMethod(`[${level}]`, msg, payload);
      } else {
        consoleMethod(`[${level}]`, payload);
      }
    };
  return {
    child(b: Record<string, unknown>) {
      return createConsoleLogger({ ...bindings, ...b });
    },
    info: log("info", console.info),
    debug: log("debug", console.debug),
    warn: log("warn", console.warn),
    error: log("error", console.error),
    trace: log("trace", console.trace),
    fatal: log("fatal", console.error),
    silent: () => {},
    level: "info",
  } as unknown as Logger;
}

export const logger: Logger = isBuildTime
  ? createConsoleLogger()
  : pino(
      isServer
        ? {
            ...(env.NODE_ENV !== "production" &&
              env.PINO_PRETTY && {
                transport: {
                  target: "pino-pretty",
                  options: {
                    colorize: true,
                  },
                },
              }),
            level: env.PINO_LOG_LEVEL || "info",
            hooks: {
              logMethod(
                inputArgs: unknown[],
                method: (...args: unknown[]) => void,
              ) {
                const redactedArgs = inputArgs.map((arg) =>
                  typeof arg === "object" && arg !== null
                    ? redactLongContent(arg)
                    : arg,
                );
                return method.apply(this, redactedArgs);
              },
            },
          }
        : {
            level: "info",
          },
    );
